import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { useEnabledQuery } from '../../../../ui/hooks/useEnabledQuery';
import { ResourceType } from '../../../enumerations/ResourceType';
import {
    useMultiParentlessResourceAdapterActions,
} from '../../../resource-base/adapters/useMultiParentlessResourceAdapterActions';
import { get } from '../../../resource-base/APIVersion1';
import { msObject } from '../../../resource-base/msObject';
import { defaultPersistableResource, PersistableResourceSchema } from '../../../resource-base/persistableResourceTypes';
import { ParamConstants } from '../../../resource-base/types';
import { UserEffectiveRightsResourceSchema } from './userEffectiveRightsResource';

export const ApplicationUserDetailsSchema = msObject({
    name: z.string().nullable(),
    email: z.string().nullable(),
    lastSyncedAt: z.string().nullable(),
});

export const UserResourceSchema = PersistableResourceSchema.extend({
    userName: z.string(),
    userDetails: ApplicationUserDetailsSchema.nullable().optional(),
    resourceType: z.literal(ResourceType.USER).default(ResourceType.USER),
});

export type UserResource = z.infer<typeof UserResourceSchema>;

export const defaultUserResource: UserResource = {
    ...defaultPersistableResource,
    resourceType: ResourceType.USER,
    userDetails: null,
    userName: '',
};

export function getUserDisplayName(user: UserResource | null | undefined): string {
    if (!user) {
        return '';
    }
    return user.userDetails?.name ? `${user.userDetails.name} (${user.userName})` : user.userName;
}

export function useUserActions() {
    const actions = useMultiParentlessResourceAdapterActions('user', ResourceType.USER, UserResourceSchema);
    const getEnabled = useEnabledQuery();

    const useGetResourceByToken = () => {
        return useQuery({
            queryKey: ['USER_BY_TOKEN'],
            queryFn: () => get(`user/token`).then((result) => actions.processGetResourceResponse(result)),
            enabled: getEnabled(true),
        });
    };

    const useGetUserEffectiveRightsOfUser = (userId: string, projectId?: string) => {
        const projectIdParam = projectId ? `?${ParamConstants.PROJECT_ID}=${projectId}` : '';
        return useQuery({
            queryKey: ['USER_EFFECTIVE_RIGHTS', projectId, userId],
            queryFn: () =>
                get(`user/${userId}/user-effective-rights${projectIdParam}`).then((result) =>
                    actions.processGetResponse(result, null, UserEffectiveRightsResourceSchema)
                ),
            enabled: getEnabled(userId),
        });
    };

    return {
        ...actions,
        useGetResourceByToken,
        useGetUserEffectiveRightsOfUser,
    };
}
